import { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Bell, CheckCircle, Clock } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function NotificationCenter() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLogs();
  }, []);

  const loadLogs = async () => {
    try {
      const response = await api.get('/notifications/logs');
      setLogs(response.data);
    } catch (error) {
      toast.error('Failed to load notification logs');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout active="notifications">
      <div data-testid="notifications-page" className="space-y-8">
        <div>
          <h1 className="text-4xl font-bold text-gray-900" style={{ fontFamily: 'Manrope' }}>
            Notification Center
          </h1>
          <p className="text-gray-600 mt-2">Track SMS notifications and customer responses</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-700"></div>
          </div>
        ) : logs.length === 0 ? (
          <Card className="bg-white border-border shadow-sm">
            <CardContent className="text-center py-12">
              <Bell className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 mb-2">No notifications sent yet</p>
              <p className="text-sm text-gray-400">
                Notifications will appear here when you start filling slots
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {logs.map((log) => (
              <Card key={log.id} className="bg-white border-border shadow-sm" data-testid={`notification-log-${log.id}`}>
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-3">
                        <h3 className="font-semibold">{log.phone}</h3>
                        {log.response ? (
                          <Badge className="bg-green-100 text-green-800">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Responded: {log.response}
                          </Badge>
                        ) : (
                          <Badge variant="outline">
                            <Clock className="h-3 w-3 mr-1" />
                            Pending
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">{log.message}</p>
                      <p className="text-xs text-gray-400">
                        Sent: {new Date(log.created_at).toLocaleString()}
                        {log.responded_at && (
                          <> • Responded: {new Date(log.responded_at).toLocaleString()}</>
                        )}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
